import { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
} from '@mui/material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import PortalProfileForm from './PortalProfileForm';

/**
 * Pantalla "Mi perfil" del portal de candidatos: permite modificar los datos
 * personales y el CV fuera del flujo de postulación. Email y documento
 * verificado se muestran solo lectura (son identificadores de la cuenta).
 */
const PortalProfileScreen = ({ candidateData, sessionToken, onSaved }) => {
  const { t } = useTranslation();

  const [profile, setProfile] = useState({
    name: candidateData?.name || '',
    // Partes del nombre (regla global: name se compone desde estas)
    names: candidateData?.names || '',
    firstLastName: candidateData?.firstLastName || '',
    secondLastName: candidateData?.secondLastName || '',
    phone: candidateData?.phone || '',
    rut: candidateData?.rut || '',
    country: candidateData?.country || '',
    profession: candidateData?.profession || '',
    currentPosition: candidateData?.currentPosition || '',
    salarioPretendido: candidateData?.salarioPretendido || '',
  });

  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState(candidateData?.cvUrl || null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleProfile = (field, value) => {
    setSuccess(false);
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleCvChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      setError(t('portal.cvInvalidType'));
      return;
    }

    setCvFile(file);
    setError(null);
    setSuccess(false);
    setUploadingCv(true);

    try {
      const formDataCv = new FormData();
      formDataCv.append('cv', file);

      const response = await api.post(
        `/public/candidates/me/cv`,
        formDataCv,
        {
          headers: {
            'x-candidate-token': sessionToken,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.response?.status_response === 'OK') {
        setCvUrl(response.data.response.cvUrl);
      } else {
        setError(t('portal.cvUploadError'));
        setCvFile(null);
      }
    } catch (err) {
      console.error(err);
      setError(t('portal.cvUploadError'));
      setCvFile(null);
    } finally {
      setUploadingCv(false);
    }
  };

  const handleSave = async () => {
    if (!profile.name.trim()) {
      setError(t('portal.nameRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await api.put(
        `/public/candidates/me`,
        profile,
        { headers: { 'x-candidate-token': sessionToken } }
      );

      if (response.data.response?.status_response === 'OK') {
        setSuccess(true);
        onSaved?.({ ...candidateData, ...profile, cvUrl });
      } else {
        setError(t('portal.profile.saveError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('portal.profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 4 }}>

      {/* Header — logo centrado como en la web de empleos */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/amandalogoh.png"
          alt="Amanda HR"
          sx={{ height: 40, mb: 3 }}
        />
        <Typography variant="h5" fontWeight={700}>
          {t('portal.profile.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('portal.profile.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>{t('portal.profile.saveSuccess')}</Alert>
      )}

      <PortalProfileForm
        profile={profile}
        onProfileChange={handleProfile}
        candidateData={candidateData}
        showIdentity
        cvFile={cvFile}
        cvUrl={cvUrl}
        uploadingCv={uploadingCv}
        onCvChange={handleCvChange}
        onClearCvFile={() => setCvFile(null)}
      />

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={saving || uploadingCv}
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}
      >
        {saving ? t('portal.profile.saving') : t('portal.profile.save')}
      </Button>

    </Box>
  );
};

export default PortalProfileScreen;
